// src/Dashboard/Dashboard.tsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Alert, Card, Nav, Tab } from "react-bootstrap";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";

import { ProfileContext } from "../Auth/ProfileProvider";
import AbpnSpinner from "../Shared/Components/AbpnSpinner";
import { FellowshipPrograms } from "../Shared/Models/FellowshipPrograms";
import { TrainingProgramTypes } from "../Shared/Models/TrainingProgram";
import NotificationManager from "../SystemNotifications/NotificationManager";

type TabKey = "active" | "alumni" | "transfer" | "reports" | "requirements";

const TabKeys = {
    Active: "active" as TabKey,
    Alumni: "alumni" as TabKey,
    Transfer: "transfer" as TabKey,
    Reports: "reports" as TabKey,
    Requirements: "requirements" as TabKey,
} as const;

const Dashboard: React.FC = () => {
    const { user, isLoadingUser } = useContext(ProfileContext);
    const [isFellowshipProgram, setIsFellowshipProgram] = useState<boolean | null>(null);
    const [showApplicationAlert, setShowApplicationAlert] = useState<boolean>(true);
    const navigate = useNavigate();
    const location = useLocation();

    const activeKeyFromLocation: TabKey = useMemo(() => {
        const path = (location.pathname || "").toLowerCase();
        if (path.includes(`/${TabKeys.Alumni}`)) return TabKeys.Alumni;
        if (path.includes(`/${TabKeys.Transfer}`)) return TabKeys.Transfer;
        if (path.includes(`/${TabKeys.Reports}`)) return TabKeys.Reports;
        if (path.includes(`/${TabKeys.Requirements}`)) return TabKeys.Requirements;
        return TabKeys.Active;
    }, [location.pathname]);

    const currentProgram = useMemo(() => {
        if (!user?.trainingPrograms?.length) return undefined;
        return user.trainingPrograms.find(
            (tp) => tp.trainingProgramId === user.selectedTrainingProgramId
        );
    }, [user]);

    useEffect(() => {
        if (isLoadingUser || !user || !user.selectedTrainingProgramId) return;
        // place any dependent fetches here
    }, [isLoadingUser, user?.selectedTrainingProgramId, user]);

    const showRequirementsTab = useMemo<boolean>(() => {
        const p = currentProgram;
        if (!p) return false;

        const t = TrainingProgramTypes;
        return [
            t.Neurology,
            t.Psychiatry,
            t.Cap,
            t.ChN,
            t.ChildNeurologyAndNeurodevelopmentalDisabilities,
            t.TripleBoard,
            t.PsychiatryAndInternalMedicine,
            t.PsychiatryAndFamilyMedicine,
            t.PsychiatryNeurology,
            t.PostPediatricPortal,
        ].includes(p.trainingProgramTypeId);
    }, [currentProgram]);

    useEffect(() => {
        if (!currentProgram) {
            setIsFellowshipProgram(false);
            return;
        }

        const isFellow = FellowshipPrograms.includes(currentProgram.trainingProgramTypeId);
        setIsFellowshipProgram(isFellow);

        if (isFellow) {
            navigate("/Reports");
        }
    }, [currentProgram, navigate]);

    if (isLoadingUser || !user) {
        console.log("Dashboard user is not ready, showing spinner...");
        return <AbpnSpinner />;
    }

    return (
        <Card className="border-pc-secondary-grey">
            <Card.Body>
                {showApplicationAlert &&
                    <Alert variant={"secondary"} onClose={() => setShowApplicationAlert(false)} dismissible>
                        <b>
                            NOTICE: Beginning in November, the ABPN will transition to a new, more secure password authentication platform. <br />
                            When logging in after the update, all preCERT users will be prompted to follow simple on-screen
                            instructions to update their password. Once complete, your account will be securely migrated and
                            further action required.
                        </b><br /><br />
                        Welcome to the ABPN preCERT system –<br /><br />
                        <b><i>ABPN recently sent our annual newsletter to all program directors which included clarifications
                            about the ABPN continuity of training policy and reconfirming psychiatry residency primary care
                            requirements. <a href="https://abpn.org/wp-content/uploads/2025/10/Annual-Program-Newsletter.pdf">Read the message from ABPN President and CEO, Dr. Jeffrey Lyness</a></i></b>
                        <br /><br />
                        Please visit abpn.org for <a href="https://abpn.org/quick-links/">application periods and exam administration dates</a> and <a href="https://abpn.org/access-residency-info/residency-training-information/abpn-precert-credentialing-system-webinar-series/"> general information about the preCERT system</a>,including FAQs and a recorded webinar.
                        The ABPN application periods and examinations take place at relatively the same time each year.  By using the preCERT system, programs agree to the following guidelines:
                        <ul>
                            <li>
                                Final attestation of completion of all training requirements
                                for residents is done only by the program director.
                            </li>
                            <li>
                                Graduate Performance reports (GPRs) are made available only
                                to program directors. GPRs are not to be shared with anyone
                                outside of the training program leadership group (i.e.,
                                program directors, Chairs, etc.) as they contain confidential
                                resident information. GPRs should not be shared with trainees
                                or training program faculty.
                            </li>
                        </ul>
                        If you have any questions, please contact us at <a href={"mailTo:questions@abpn.org?subject=ABPN Account Help"}>questions@abpn.org</a>.
                    </Alert>

                }
                <NotificationManager />

                <Tab.Container activeKey={activeKeyFromLocation}>
                    <Nav variant="tabs">
                        <Nav.Item>
                            <Nav.Link
                                className={isFellowshipProgram ? "d-none" : ""}
                                eventKey={TabKeys.Active}
                                as={Link}
                                to="/"
                            >
                                Active Roster
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link
                                className={isFellowshipProgram ? "d-none" : ""}
                                eventKey={TabKeys.Alumni}
                                as={Link}
                                to="/Alumni"
                            >
                                Alumni Roster
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link
                                className={isFellowshipProgram ? "d-none" : ""}
                                eventKey={TabKeys.Transfer}
                                as={Link}
                                to="/Transfer"
                            >
                                Transfer
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link
                                eventKey={TabKeys.Reports}
                                as={Link}
                                to="/Reports"
                            >
                                Reports
                            </Nav.Link>
                        </Nav.Item>
                        {showRequirementsTab && (
                            <Nav.Item>
                                <Nav.Link
                                    eventKey={TabKeys.Requirements}
                                    as={Link}
                                    to="/Requirements"
                                >
                                    Requirements
                                </Nav.Link>
                            </Nav.Item>
                        )}
                    </Nav>
                </Tab.Container>

                <Tab.Content className="mt-2">
                    <Outlet />
                </Tab.Content>
            </Card.Body>
        </Card>
    );
};

export default Dashboard;
